"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SugerenciasLineasPersonalizadasResponseSchema = exports.SugerenciaLineaPersonalizadaSchema = exports.LineaPersonalizadaInputSchema = exports.MAX_LARGO_LINEA_PERSONALIZADA = exports.MAX_LINEAS_PERSONALIZADAS_HISTORICO = exports.MAX_LINEAS_PERSONALIZADAS = void 0;
exports.personalizadasDentroDelTope = personalizadasDentroDelTope;
exports.normalizarTextoLinea = normalizarTextoLinea;
exports.agruparLineasPorNombre = agruparLineasPorNombre;
exports.mostrarUnidadesEnLineas = mostrarUnidadesEnLineas;
exports.opcionesTextoLibre = opcionesTextoLibre;
exports.agruparSugerenciasLineas = agruparSugerenciasLineas;
exports.canonizarTextoLinea = canonizarTextoLinea;
exports.hayTextosDuplicados = hayTextosDuplicados;
const zod_1 = require("zod");
// ─── Display helpers for research lines: pure, shared by web and API ──────────
//
// Two concerns live here: how the lines of a researcher are grouped and labelled
// across units (RLM-03), and the limits/matching of free-text custom lines (CRL).
// Matching is accent-, case- and whitespace-insensitive, like the directory search.
// ──────────────────────────────────────────────────────────────────────────────
/** A researcher holds at most this many custom (free-text) research lines. */
exports.MAX_LINEAS_PERSONALIZADAS = 3;
/**
 * The cap before it was lowered to `MAX_LINEAS_PERSONALIZADAS`: the most lines a profile saved under it can hold.
 * The payload schema accepts up to this many so such a profile can still send its state back; the real cap is
 * applied by `personalizadasDentroDelTope` against what is stored.
 */
exports.MAX_LINEAS_PERSONALIZADAS_HISTORICO = 5;
/** Longest custom line, in characters, after trimming. */
exports.MAX_LARGO_LINEA_PERSONALIZADA = 150;
/**
 * Whether a save with `deseadas` custom lines respects the cap, given `guardadas` already stored. Up to the cap is
 * always fine; past it only while the save does not hold MORE lines than are stored, so a profile saved under the
 * old cap can still be edited, reordered or trimmed, but never grows until it is back within the cap.
 */
function personalizadasDentroDelTope(deseadas, guardadas) {
    return deseadas <= Math.max(exports.MAX_LINEAS_PERSONALIZADAS, guardadas);
}
/** One custom line as the lines modal sends it; new lines carry no id. */
exports.LineaPersonalizadaInputSchema = zod_1.z.object({
    idLineaPersonalizada: zod_1.z.number().int().positive().optional(),
    texto: zod_1.z
        .string()
        .trim()
        .min(1, 'Escribe el texto de la línea.')
        .max(exports.MAX_LARGO_LINEA_PERSONALIZADA, `La línea puede tener como máximo ${exports.MAX_LARGO_LINEA_PERSONALIZADA} caracteres.`),
});
// Combining diacritical marks (U+0300-U+036F). Built with fromCharCode so no raw combining
// characters are embedded in the source (same convention as `apps/web/src/lib/search.ts`).
const MARCAS_DIACRITICAS_RE = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
const ESPACIOS_RE = /\s+/g;
/**
 * Comparison key of a line name or custom text: lowercase, no diacritics, single spaces,
 * trimmed. `"  Glaciología   ANDINA "` becomes `"glaciologia andina"`.
 */
function normalizarTextoLinea(texto) {
    return texto
        .normalize('NFD')
        .replace(MARCAS_DIACRITICAS_RE, '')
        .toLowerCase()
        .replace(ESPACIOS_RE, ' ')
        .trim();
}
/**
 * Presentational de-duplication: lines whose names are equal ignoring case, accents and
 * whitespace become ONE group whose units are the union. Groups keep first-seen order.
 * The underlying assignments stay separate; this only shapes what is listed.
 */
function agruparLineasPorNombre(lineas) {
    const grupos = new Map();
    for (const linea of lineas) {
        const clave = normalizarTextoLinea(linea.nombre);
        let grupo = grupos.get(clave);
        if (!grupo) {
            grupo = { clave, nombre: linea.nombre.trim(), unidades: [] };
            grupos.set(clave, grupo);
        }
        for (const unidad of linea.unidades) {
            if (!grupo.unidades.includes(unidad))
                grupo.unidades.push(unidad);
        }
    }
    return [...grupos.values()];
}
/** The unit label is shown only when the lines span more than one distinct unit (RLM-03). */
function mostrarUnidadesEnLineas(grupos) {
    return new Set(grupos.flatMap((grupo) => grupo.unidades)).size > 1;
}
/**
 * Options for the directory's custom-line filter: the texts de-duplicated ignoring case and
 * accents, with how many times each appears, sorted by key. Blank texts are skipped. Each option
 * is labelled with its most frequent spelling (the first seen on a tie).
 */
function opcionesTextoLibre(textos) {
    const grupos = new Map();
    for (const texto of textos) {
        const clave = normalizarTextoLinea(texto);
        if (!clave)
            continue;
        const forma = texto.trim().replace(ESPACIOS_RE, ' ');
        const grupo = grupos.get(clave) ?? { formas: new Map(), cuenta: 0 };
        grupo.formas.set(forma, (grupo.formas.get(forma) ?? 0) + 1);
        grupo.cuenta += 1;
        grupos.set(clave, grupo);
    }
    return [...grupos]
        .map(([clave, { formas, cuenta }]) => ({ clave, etiqueta: formaMasFrecuente(formas), cuenta }))
        .sort((a, b) => (a.clave < b.clave ? -1 : a.clave > b.clave ? 1 : 0));
}
/** The spelling with the highest count; `Map` keeps insertion order, so the first seen wins ties. */
function formaMasFrecuente(formas) {
    let ganadora = '';
    let max = 0;
    for (const [forma, veces] of formas) {
        if (veces > max) {
            ganadora = forma;
            max = veces;
        }
    }
    return ganadora;
}
/** A custom line other researchers already use, with how many of them. */
exports.SugerenciaLineaPersonalizadaSchema = zod_1.z.object({
    texto: zod_1.z.string().min(1),
    investigadores: zod_1.z.number().int().positive(),
});
exports.SugerenciasLineasPersonalizadasResponseSchema = zod_1.z.object({
    sugerencias: zod_1.z.array(exports.SugerenciaLineaPersonalizadaSchema),
});
/**
 * The custom lines researchers already use, one suggestion per text equal after case, accents and
 * whitespace, so the same line converges to one spelling. `investigadores` counts DISTINCT researchers.
 * Each suggestion is labelled with its most frequent spelling (single spaces; the first seen on a tie, so
 * pass the rows oldest first for a stable label). Ordered by usage descending, then by the comparison key.
 * Blank texts are skipped.
 */
function agruparSugerenciasLineas(filas) {
    const grupos = new Map();
    for (const { idInvestigador, texto } of filas) {
        const clave = normalizarTextoLinea(texto);
        if (!clave)
            continue;
        const forma = texto.trim().replace(ESPACIOS_RE, ' ');
        const grupo = grupos.get(clave) ?? { formas: new Map(), investigadores: new Set() };
        grupo.formas.set(forma, (grupo.formas.get(forma) ?? 0) + 1);
        grupo.investigadores.add(idInvestigador);
        grupos.set(clave, grupo);
    }
    return [...grupos]
        .map(([clave, { formas, investigadores }]) => ({
        clave,
        texto: formaMasFrecuente(formas),
        investigadores: investigadores.size,
    }))
        .sort((a, b) => b.investigadores - a.investigadores || (a.clave < b.clave ? -1 : a.clave > b.clave ? 1 : 0))
        .map(({ texto, investigadores }) => ({ texto, investigadores }));
}
/**
 * The spelling to store for a typed custom line: the existing one when `texto` equals a suggestion after
 * case, accents and whitespace (NOT fuzzy: "Hola como ta" never becomes "Hola como tas"), otherwise `texto`
 * as given.
 */
function canonizarTextoLinea(texto, sugerencias) {
    const clave = normalizarTextoLinea(texto);
    if (!clave)
        return texto;
    return sugerencias.find((sugerencia) => normalizarTextoLinea(sugerencia.texto) === clave)?.texto ?? texto;
}
/** True when two custom texts are equal after trimming and ignoring case and accents. Blanks are ignored. */
function hayTextosDuplicados(textos) {
    const claves = textos.map(normalizarTextoLinea).filter(Boolean);
    return new Set(claves).size !== claves.length;
}
